import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminTeamView, AdminUserView } from '@retro/shared';
import clsx from 'clsx';
import { Search } from 'lucide-react';
import { ApiError, api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { useCurrentUser } from '../lib/auth.js';
import { AppShell } from '../components/AppShell.js';
import { Avatar, Modal, Spinner, Stat } from '../components/ui.js';
import { CopyRow } from '../components/retro/InviteDialog.js';

/**
 * Platform administration: accounts and every team on the instance.
 *
 * Reachable only by administrators; the API answers 404 for everyone else, so an
 * ordinary account that guesses the URL sees the same thing as for a typo.
 */
export function AdminPage() {
  const { data: me } = useCurrentUser();
  const [tab, setTab] = useState<'users' | 'teams'>('users');

  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: api.admin.users });
  const teams = useQuery({ queryKey: ['admin', 'teams'], queryFn: api.admin.teams });

  // Only routed for a signed-in admin, so this is a formality rather than a guard.
  if (!me) return <Spinner label="Loading…" />;

  const activeUsers = (users.data?.users ?? []).filter((u) => !u.deletedAt);
  const teamList = teams.data?.teams ?? [];

  const tabs = [
    { id: 'users' as const, label: 'People' },
    { id: 'teams' as const, label: 'Teams' },
  ];

  return (
    <AppShell user={me} crumb="Administration">
      <div className="ph">
        <div>
          <span className="eyebrow">Administration</span>
          <h2>Access &amp; governance</h2>
          <p className="lede">
            For workspace administrators. Accounts and teams across the whole instance, including
            teams you are not in.
          </p>
        </div>
      </div>

      <div className="stats">
        <Stat
          label="People with access"
          value={users.data ? activeUsers.length : '—'}
          note="with a retro.blue account"
        />
        <Stat label="Teams" value={teams.data ? teamList.length : '—'} note="all active" />
      </div>

      <div className="tabs" role="tablist" aria-label="Admin sections">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'users' ? <People myId={me.id} users={users} /> : <Teams teams={teams} />}
    </AppShell>
  );
}

/* ---------------------------------- people -------------------------------- */

function People({
  myId,
  users,
}: {
  myId: string;
  users: ReturnType<typeof useQuery<Awaited<ReturnType<typeof api.admin.users>>>>;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<AdminUserView | null>(null);
  const [filter, setFilter] = useState('');
  const [resetTarget, setResetTarget] = useState<AdminUserView | null>(null);
  const [resetLink, setResetLink] = useState<{ resetLink: string; expiresAt: string } | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  const onError = (caught: unknown) =>
    setError(caught instanceof ApiError ? caught.message : 'Something went wrong.');

  const setAdmin = useMutation({
    mutationFn: ({ id, isAdmin }: { id: string; isAdmin: boolean }) => api.admin.setAdmin(id, isAdmin),
    onSuccess: invalidate,
    onError,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.admin.deleteUser(id),
    onSuccess: () => {
      setConfirming(null);
      void invalidate();
    },
    onError,
  });
  const restore = useMutation({
    mutationFn: (id: string) => api.admin.restoreUser(id),
    onSuccess: invalidate,
    onError,
  });
  const createResetLink = useMutation({
    mutationFn: (id: string) => api.admin.createResetLink(id),
    onSuccess: (data) => setResetLink(data),
    onError,
  });
  const revokeResetLink = useMutation({
    mutationFn: (id: string) => api.admin.revokeResetLink(id),
    onSuccess: () => {
      setResetLink(null);
      setResetTarget(null);
      toast.info('That link no longer works.');
    },
    onError,
  });

  if (users.isLoading) return <Spinner label="Loading accounts…" />;
  if (users.error) return <ErrorNote message="Could not load accounts." />;

  const list = users.data?.users ?? [];
  // Offering "Remove admin" when it cannot succeed is worse than not offering it,
  // so the last one is disabled with the reason attached rather than left to fail
  // against the server's rail.
  const activeAdmins = list.filter((u) => u.isAdmin && !u.deletedAt).length;

  const needle = filter.trim().toLowerCase();
  const visible = needle
    ? list.filter(
        (u) => u.name.toLowerCase().includes(needle) || u.email.toLowerCase().includes(needle),
      )
    : list;

  return (
    <>
      {error ? <ErrorNote message={error} /> : null}

      <div className="filters">
        <label className="search">
          <Search size={15} className="i" aria-hidden />
          <input
            className="input"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Search people…"
            aria-label="Search people"
          />
        </label>
        <span className="text-[12px] text-muted">
          retro.blue shows no activity metrics about individuals.
        </span>
      </div>

      <div className="card table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Person</th>
              <th>Teams</th>
              <th>Account</th>
              <th>Access</th>
              <th className="text-right" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {visible.map((user) => (
              <tr key={user.id} className={clsx(user.deletedAt && 'opacity-60')}>
                <td>
                  <div className="person">
                    <Avatar emoji={user.avatar} name={user.name} />
                    <div className="min-w-0">
                      <strong className="truncate">
                        {user.name}
                        {user.id === myId ? <span className="text-muted"> (you)</span> : null}
                      </strong>
                      <small className="truncate">{user.email}</small>
                    </div>
                  </div>
                </td>
                <td className="text-muted">
                  {user.teamCount} {user.teamCount === 1 ? 'team' : 'teams'}
                </td>
                <td className="text-muted">{user.isAdmin ? 'Admin' : 'Member'}</td>
                <td>
                  {user.deletedAt ? (
                    <span className="badge badge-warn">Deleted</span>
                  ) : (
                    <span className="badge badge-ok">Active</span>
                  )}
                </td>
                <td>
                  <div className="flex justify-end gap-1.5">
                    {user.deletedAt ? (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => restore.mutate(user.id)}
                        disabled={restore.isPending}
                      >
                        Restore
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => setAdmin.mutate({ id: user.id, isAdmin: !user.isAdmin })}
                          disabled={setAdmin.isPending || (user.isAdmin && activeAdmins <= 1)}
                          title={
                            user.isAdmin && activeAdmins <= 1
                              ? 'The only administrator. Promote someone else first.'
                              : undefined
                          }
                        >
                          {user.isAdmin ? 'Remove admin' : 'Make admin'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => {
                            setResetTarget(user);
                            setResetLink(null);
                          }}
                        >
                          Reset password
                        </button>
                        {user.id === myId ? (
                          <span className="badge">You</span>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger-text"
                            onClick={() => setConfirming(user)}
                          >
                            Remove access
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {visible.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-muted">
                  Nobody matches “{filter}”.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Modal
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title={`Delete ${confirming?.name ?? ''}?`}
        width="sm"
      >
        <p className="text-sm text-muted">
          They will not be able to sign in, and they are removed from every team. What they wrote in
          past retrospectives stays, so those reports still read properly.
        </p>
        <p className="mt-2 text-sm text-muted">This can be undone — the account can be restored.</p>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            className="btn btn-danger flex-1"
            onClick={() => confirming && remove.mutate(confirming.id)}
            disabled={remove.isPending}
          >
            {remove.isPending ? 'Deleting…' : 'Delete account'}
          </button>
          <button type="button" className="btn" onClick={() => setConfirming(null)}>
            Cancel
          </button>
        </div>
      </Modal>

      <Modal
        open={resetTarget !== null}
        onClose={() => {
          setResetTarget(null);
          setResetLink(null);
        }}
        title={`Reset ${resetTarget?.name ?? ''}'s password`}
        width="sm"
      >
        {resetLink ? (
          <div className="space-y-3">
            <p className="text-sm text-ink/70">
              Give this link to {resetTarget?.name} privately — you are the delivery person. It stops
              working the moment it is used, and it expires{' '}
              {new Date(resetLink.expiresAt).toLocaleString()}.
            </p>
            <CopyRow
              label="Password reset link"
              hint="Creating a new link invalidates the previous one."
              link={resetLink.resetLink}
            />
            <button
              type="button"
              className="btn-ghost w-full"
              onClick={() => resetTarget && revokeResetLink.mutate(resetTarget.id)}
              disabled={revokeResetLink.isPending}
            >
              {revokeResetLink.isPending ? 'Revoking…' : 'Revoke link'}
            </button>
          </div>
        ) : (
          <>
            <p className="text-sm text-ink/70">
              This creates a one-time link that lets {resetTarget?.name} set a new password. No email
              is sent — you pass the link along yourself. It expires in an hour, and making a new one
              invalidates this one.
            </p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                className="btn-primary flex-1"
                onClick={() => resetTarget && createResetLink.mutate(resetTarget.id)}
                disabled={createResetLink.isPending}
              >
                {createResetLink.isPending ? 'Creating…' : 'Create link'}
              </button>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  setResetTarget(null);
                  setResetLink(null);
                }}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}

/* ---------------------------------- teams --------------------------------- */

function Teams({
  teams,
}: {
  teams: ReturnType<typeof useQuery<Awaited<ReturnType<typeof api.admin.teams>>>>;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<AdminTeamView | null>(null);
  const [filter, setFilter] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [renaming, setRenaming] = useState<AdminTeamView | null>(null);
  const [renameName, setRenameName] = useState('');

  const onError = (caught: unknown) =>
    setError(caught instanceof ApiError ? caught.message : 'Something went wrong.');

  // Team creation is administrator-only, so this tab is its home. The 'teams'
  // invalidation keeps the dashboard's own team dropdown in step.
  const createTeam = useMutation({
    mutationFn: (name: string) => api.teams.create(name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
      setCreatingTeam(false);
      setNewTeamName('');
    },
    onError,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.admin.deleteTeam(id),
    onSuccess: () => {
      setConfirming(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
    onError,
  });

  // Renaming goes through the ordinary teams endpoint rather than an
  // admin-specific one: an admin is a facilitator of every team (see
  // effectiveTeamRole), so the same PATCH a team's own facilitator uses is
  // all this needs. The 'teams' invalidation keeps the dashboard's team
  // dropdown in step, and 'team' refreshes the renamed team's own page if
  // it is open somewhere else.
  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.teams.rename(id, name),
    onSuccess: (_result, target) => {
      setRenaming(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
      void queryClient.invalidateQueries({ queryKey: ['team', target.id] });
    },
    onError,
  });

  const join = useMutation({
    mutationFn: (id: string) => api.admin.joinTeam(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    onError,
  });

  if (teams.isLoading) return <Spinner label="Loading teams…" />;
  if (teams.error) return <ErrorNote message="Could not load teams." />;

  const list = teams.data?.teams ?? [];

  const needle = filter.trim().toLowerCase();
  const visible = needle ? list.filter((t) => t.name.toLowerCase().includes(needle)) : list;

  return (
    <>
      {error ? <ErrorNote message={error} /> : null}

      <div className="filters">
        <label className="search">
          <Search size={15} className="i" aria-hidden />
          <input
            className="input"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Search teams…"
            aria-label="Search teams"
          />
        </label>
        <button type="button" className="btn" onClick={() => setCreatingTeam(true)}>
          Create team
        </button>
      </div>

      <div className="card table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Team</th>
              <th>Members</th>
              <th>Retrospectives</th>
              <th>Facilitators</th>
              <th className="text-right" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {visible.map((team) => (
              <tr key={team.id}>
                <td>
                  <div className="person">
                    <Avatar emoji={team.avatar} name={team.name} />
                    <strong className="truncate">{team.name}</strong>
                  </div>
                </td>
                <td className="text-muted">{team.memberCount}</td>
                <td className="text-muted">{team.retroCount}</td>
                <td className="text-muted">
                  {team.facilitators.length > 0 ? team.facilitators.join(', ') : '—'}
                </td>
                <td>
                  <div className="flex justify-end gap-1.5">
                    <Link to={`/teams/${team.id}`} className="btn btn-sm">
                      Manage
                    </Link>
                    {team.isMember ? null : (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => join.mutate(team.id)}
                        disabled={join.isPending}
                      >
                        Join
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => {
                        setRenaming(team);
                        setRenameName(team.name);
                      }}
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger-text"
                      onClick={() => setConfirming(team)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {visible.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-muted">
                  {list.length === 0 ? 'No teams yet.' : `No team matches “${filter}”.`}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Modal
        open={creatingTeam}
        onClose={() => setCreatingTeam(false)}
        title="Create a team"
        width="sm"
      >
        <p className="mb-3 text-sm text-ink/60">
          You are seated as its facilitator — share the invite link from the team page to add
          members.
        </p>
        <input
          className="input"
          value={newTeamName}
          onChange={(event) => setNewTeamName(event.target.value)}
          placeholder="Platform squad"
          maxLength={80}
          aria-label="Team name"
        />
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            className="btn btn-primary flex-1"
            disabled={!newTeamName.trim() || createTeam.isPending}
            onClick={() => createTeam.mutate(newTeamName.trim())}
          >
            {createTeam.isPending ? 'Creating…' : 'Create team'}
          </button>
          <button type="button" className="btn" onClick={() => setCreatingTeam(false)}>
            Cancel
          </button>
        </div>
      </Modal>

      <Modal
        open={renaming !== null}
        onClose={() => setRenaming(null)}
        title={`Rename ${renaming?.name ?? ''}`}
        width="sm"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setRenaming(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!renameName.trim() || rename.isPending}
              onClick={() => renaming && rename.mutate({ id: renaming.id, name: renameName.trim() })}
            >
              {rename.isPending ? 'Renaming…' : 'Rename'}
            </button>
          </>
        }
      >
        <input
          className="input"
          value={renameName}
          onChange={(event) => setRenameName(event.target.value)}
          placeholder="Team name"
          maxLength={80}
          aria-label="Team name"
        />
      </Modal>

      <Modal
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title={`Delete ${confirming?.name ?? ''}?`}
        width="sm"
      >
        <p className="text-sm text-muted">
          This removes the team and{' '}
          <strong>
            all {confirming?.retroCount ?? 0} of its retrospectives, with their comments and action
            items
          </strong>
          . Unlike deleting an account, it cannot be undone.
        </p>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            className="btn btn-danger flex-1"
            onClick={() => confirming && remove.mutate(confirming.id)}
            disabled={remove.isPending}
          >
            {remove.isPending ? 'Deleting…' : 'Delete team and its history'}
          </button>
          <button type="button" className="btn" onClick={() => setConfirming(null)}>
            Cancel
          </button>
        </div>
      </Modal>
    </>
  );
}

function ErrorNote({ message }: { message: string }) {
  return (
    <p role="alert" className="err-msg">
      {message}
    </p>
  );
}
